import { bopApi } from '@/lib/api/client';

const BASE_PATH = 'v1/compliance/whitelisted-payout-addresses';

export const WHITELISTED_ADDRESS_TYPES = ['IBAN', 'Blockchain'] as const;
export type WhitelistedPayoutAddressType = (typeof WHITELISTED_ADDRESS_TYPES)[number];

/**
 * Raw DynamoDB item inside GET responses. Audit rows share the same partition
 * (sort keys `create_<uuid>` / `update_<uuid>` / `delete_<uuid>`) and carry
 * `note` / `requester` — the optional fields exist so the client-side filter can
 * recognise them if one ever leaks through the server's sort-key heuristic.
 */
export interface RawWhitelistedAddressItem {
  p: 'whitelisted-payout-address';
  /** sort key — the record UUID (v4) on address rows */
  s: string;
  name: string;
  currency: string;
  type: WhitelistedPayoutAddressType;
  /** null when type Blockchain */
  iban: string | null;
  /** null when type IBAN */
  wallet_address: string | null;
  /** ISO timestamp */
  created: string;
  created_by: string;
  /** ISO timestamp */
  updated: string;
  updated_by: string;
  /** audit rows only */
  note?: string;
  /** audit rows only */
  requester?: string;
}

/** UI shape: `s`→`uuid`, `created`→`created_at`, `updated`→`updated_at`, null→undefined */
export interface WhitelistedPayoutAddress {
  uuid: string;
  name: string;
  currency: string;
  type: WhitelistedPayoutAddressType;
  iban?: string;
  wallet_address?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface CreateWhitelistedPayoutAddressRequest {
  name: string;
  currency: string;
  type: WhitelistedPayoutAddressType;
  iban?: string;
  wallet_address?: string;
  comment: string;
}

export interface UpdateWhitelistedPayoutAddressRequest {
  uuid: string;
  name?: string;
  currency?: string;
  type?: WhitelistedPayoutAddressType;
  iban?: string;
  wallet_address?: string;
  comment: string;
}

export interface DeleteWhitelistedPayoutAddressRequest {
  uuid: string;
  comment: string;
}

/** What the create/edit form produces — the payload builders below reshape it for the wire */
export interface AddressFormValues {
  name: string;
  currency: string;
  type: WhitelistedPayoutAddressType;
  iban: string;
  wallet_address: string;
  comment: string;
}

interface WhitelistedAddressesResponse {
  whitelistedAddresses: RawWhitelistedAddressItem[];
}

interface CreateWhitelistedAddressResponse {
  message: string;
  uuid: string;
}

interface MessageResponse {
  message: string;
}

/**
 * GET reshaping (legacy Nuxt `index.get.ts`, moved client-side). The audit-row
 * filter (`!note && !requester`) is belt-and-braces on top of the server's
 * UUID-shaped-sort-key filter — deliberate defence-in-depth, keep both.
 */
export const mapWhitelistedAddresses = (items: RawWhitelistedAddressItem[] | null | undefined): WhitelistedPayoutAddress[] =>
  (items ?? [])
    .filter((item) => !item.note && !item.requester)
    .map((item) => ({
      uuid: item.s,
      name: item.name,
      currency: item.currency,
      type: item.type,
      iban: item.iban || undefined,
      wallet_address: item.wallet_address || undefined,
      created_at: item.created,
      updated_at: item.updated,
      created_by: item.created_by,
      updated_by: item.updated_by,
    }));

/** POST body: name/currency/type/comment plus only the identifier matching the type */
export const buildCreatePayload = (values: AddressFormValues): CreateWhitelistedPayoutAddressRequest => ({
  name: values.name,
  currency: values.currency,
  type: values.type,
  comment: values.comment,
  ...(values.type === 'IBAN' ? { iban: values.iban } : { wallet_address: values.wallet_address }),
});

/**
 * Changed-fields-only PUT (legacy parity): unchanged name/currency/type stay out of
 * the payload so the server's audit note lists only real changes; the identifier for
 * the currently selected type is ALWAYS sent, even when unchanged.
 *
 * Deliberate fix: legacy never cleared the opposite identifier on a type
 * switch, so an IBAN→Blockchain edit left the stale `iban` attribute on the Dynamo
 * record (the PUT is partial) and the details dialog then showed both identifiers.
 * When `type` changes we also send the opposite field as `''` — it passes the
 * lambda's `!== undefined` check and overwrites the stale value, and upstream
 * validation only inspects the field matching the new type.
 */
export const buildUpdatePayload = (selected: WhitelistedPayoutAddress, values: AddressFormValues): UpdateWhitelistedPayoutAddressRequest => {
  const isTypeChanged = values.type !== selected.type;
  return {
    uuid: selected.uuid,
    comment: values.comment,
    ...(values.name !== selected.name ? { name: values.name } : {}),
    ...(values.currency !== selected.currency ? { currency: values.currency } : {}),
    ...(isTypeChanged ? { type: values.type } : {}),
    ...(values.type === 'IBAN'
      ? { iban: values.iban, ...(isTypeChanged ? { wallet_address: '' } : {}) }
      : { wallet_address: values.wallet_address, ...(isTypeChanged ? { iban: '' } : {}) }),
  };
};

export const getWhitelistedPayoutAddresses = () => bopApi.get(BASE_PATH).json<WhitelistedAddressesResponse>();

export const createWhitelistedPayoutAddress = (body: CreateWhitelistedPayoutAddressRequest) => bopApi.post(BASE_PATH, { json: body }).json<CreateWhitelistedAddressResponse>();

// uuid travels in the path; a body `uuid` would be ignored upstream, so it is stripped
export const updateWhitelistedPayoutAddress = ({ uuid, ...body }: UpdateWhitelistedPayoutAddressRequest) =>
  bopApi.put(`${BASE_PATH}/${uuid}`, { json: body }).json<MessageResponse>();

export const deleteWhitelistedPayoutAddress = ({ uuid, comment }: DeleteWhitelistedPayoutAddressRequest) =>
  bopApi.delete(`${BASE_PATH}/${uuid}`, { json: { comment } }).json<MessageResponse>();
